// Title-case a finished string (used as a save-time safety pass).
export const toPascalCase = (str) => {
  if (!str || typeof str !== 'string') return '';
  return str.trim().split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
};

// Live-typing variant: preserves a single trailing space so the user can keep typing.
export const normaliseText = (val) => {
  if (!val) return '';
  const trailingSpace = val.endsWith(' ') ? ' ' : '';
  const words = val.trimStart().split(/\s+/).filter(Boolean);
  return words.map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ') + trailingSpace;
};

// Field limits, shared by every guest form so the printed card and the
// compact guest rows always fit.
export const MAX_NAME = 15;
// A name is title-cased AND cut to 15 characters as it is typed.
export const nameInput = (val) => normaliseText(val).slice(0, MAX_NAME);
// Exactly 10 digits: a pasted "+91 98222 10195" or "091-98222-10195" keeps its
// last 10, so the country code and the spaces never reach the database.
export const tenDigits = (val) => {
  let d = String(val == null ? '' : val).replace(/\D/g, '');
  if (d.length > 10) d = d.slice(-10);            // drop 91 / 0 / 0091 prefixes
  return d.slice(0, 10);
};

export const SIDES = ['Common', 'Groom', 'Bride'];
export const EVENT_TYPES = [
  'Birthday Party', 'Marriage', 'Engagement', 'Anniversary', 'Corporate Event',
  'Thread Ceremony', 'Cocktail Party', 'Reception', 'Naming Ceremony',
  'Housewarming', 'Baby Shower', 'Other',
];
// Sub-events ("functions") that make up one event.
export const FUNCTION_TYPES = [
  'Opening / Welcome', 'Main Celebration', 'Dinner', 'Reception', 'Cocktail Party',
  'Cake Cutting', 'Games', 'Return Gifts', 'Haldi', 'Mehndi', 'Sangeet',
  'Wedding', 'Wedding Cum Reception', 'Ring Ceremony', 'Conference', 'Awards', 'Other',
];
export const isMarriageEventType = (value) => {
  const type = String(value || '').trim().toLowerCase();
  return type === 'marriage' || type === 'wedding';
};
// Member invite lifecycle (the "mark" statuses).
// Function Type "Other" → the user types a new name (saved as a new type).
export const OTHER_FUNCTION_TYPE = 'Other';

// FunctionTime is stored as 24-h "HH:MM" (from <input type="time">). Older rows
// hold free text like "7:00 PM" — shown as-is. fmtTime → "7:00 PM" for display.
// "7:00 PM" / "19:00:00" / "19:00" → "19:00" so any stored value binds to the clock input.
export const toTimeInput = (v) => {
  const s = String(v || '').trim();
  const m = /^(\d{1,2}):(\d{2})(?::\d{2})?\s*([AaPp][Mm])?$/.exec(s);
  if (!m) return '';
  let h = Number(m[1]);
  if (m[3]) { const pm = /p/i.test(m[3]); if (pm && h < 12) h += 12; if (!pm && h === 12) h = 0; }
  if (h > 23) return '';
  return `${String(h).padStart(2, '0')}:${m[2]}`;
};
// Display: any stored value → "7:00 PM"; free text that is not a time is shown as-is.
export const fmtTime = (v) => {
  const t = toTimeInput(v);
  if (!t) return v || '';
  const [h, mm] = t.split(':').map(Number);
  return `${h % 12 === 0 ? 12 : h % 12}:${String(mm).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

export const INVITE_STATUSES = ['Pending', 'Invited', 'Confirmed', 'Declined'];

// Local calendar date as YYYY-MM-DD (not UTC — the app runs in IST).
export const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
// Local date AND time as 'YYYY-MM-DD HH:MM:00' - when a card was handed over.
export const nowISO = () => {
  const d = new Date();
  return `${todayISO()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:00`;
};
// A function whose date is before today is "over": guests can no longer be
// invited to it (it stays visible in filters / headcount for the record).
export const isPastFunction = (fn) => {
  const d = String(fn?.FunctionDate || '').slice(0, 10);
  return !!d && d < todayISO();
};
// AUTO functions — ticked for every guest automatically:
//   • the Reception: EVERY guest is invited to it (Sahil weds Diksha, 2026-10);
//   • the Wedding: nearly every guest.
// Matched on the function's type or its own name ("Receiption" spelling too).
export const isWeddingFunction = (fn) =>
  /wedding/i.test(`${fn?.FunctionType || ''} ${fn?.FunctionTypeName || ''} ${fn?.FunctionName || ''}`);
export const isReceptionFunction = (fn) =>
  /recep?ti?on|receiption/i.test(`${fn?.FunctionType || ''} ${fn?.FunctionTypeName || ''} ${fn?.FunctionName || ''}`);
export const isAutoFunction = (fn) => isWeddingFunction(fn) || isReceptionFunction(fn);
const openAutoIds = (pool) => (pool || []).filter(f => isAutoFunction(f) && !isPastFunction(f)).map(f => String(f.FunctionId));
// The same list, for a caller that needs to ask "does this set leave an auto
// function out?" - a repeated set that does must keep it out.
export const openAutoFunctionIds = openAutoIds;

// Tick / untick one function in a guest's list with the AUTO rule:
//   • ticking ANY other function also ticks the (still open) Wedding and
//     Reception functions found in `pool` - the functions this guest may be
//     invited to;
//   • unticking the Wedding or the Reception is allowed and sets `weddingOff`
//     ("auto off"), after which they are never added back for that guest
//     (ticking one again by hand clears the flag).
// Returns { ids, weddingOff }; ids are strings. (Name kept for the callers.)
export function toggleWithWedding(ids, functionId, pool, weddingOff = false) {
  const id = String(functionId);
  const current = (ids || []).map(String);
  const fn = (pool || []).find(f => String(f.FunctionId) === id);
  const isAuto = !!fn && isAutoFunction(fn);
  if (current.includes(id)) {
    return { ids: current.filter(x => x !== id), weddingOff: weddingOff || isAuto };
  }
  const next = [...current, id];
  if (isAuto) return { ids: next, weddingOff: false };
  if (!weddingOff) openAutoIds(pool).forEach(a => { if (!next.includes(a)) next.push(a); });
  return { ids: next, weddingOff };
}

// A ready-made list (a preset filter, a copied row): if it holds any other
// function, make sure the Wedding and the Reception are in it too.
export function withWedding(ids, pool) {
  const current = (ids || []).map(String);
  const autos = openAutoIds(pool);
  const hasOther = current.some(id => !autos.includes(id));
  if (!hasOther) return current;
  return [...current, ...autos.filter(a => !current.includes(a))];
}

// What a NEW guest starts with: the given list (filter preset / last used)
// with the auto rule applied, and the Reception ALWAYS ticked - everyone is
// invited to it.
export function newGuestFunctionIds(ids, pool) {
  const out = withWedding(ids, pool);
  (pool || []).filter(f => isReceptionFunction(f) && !isPastFunction(f))
    .forEach(f => { const r = String(f.FunctionId); if (!out.includes(r)) out.push(r); });
  return out;
}

// ── Invitation cards ──────────────────────────────────────────────────────
// Extra cards a family head asks for carry unknown guests; when the head does
// not say how many people, each extra card counts as this many persons.
export const EXTRA_PERSONS_PER_CARD = 2;
export const CARD_KINDS = ['Card', 'Insert'];           // an Insert is extra paper inside a card
export const CARD_STATUSES = ['Pending', 'Delivered'];
// Where a function stands (Functions page dropdown + form).
export const FUNCTION_STATUSES = ['Planned', 'Confirmed', 'Done', 'Cancelled'];

// ── Expenses ──────────────────────────────────────────────────────────────
export const EXPENSE_CATEGORIES = [
  'Hall / Venue', 'Food / Catering', 'Decoration', 'Clothes', 'Jewellery', 'Cards & Printing',
  'Photography', 'Music / DJ', 'Priest / Pooja', 'Gifts', 'Travel & Stay', 'Other',
];
export const PAYMENT_MODES = ['Cash', 'UPI', 'Bank', 'Cheque', 'Card'];

// Who can see a guest a member added: Common = all members, Private = only the adder.
export const VISIBILITIES = ['Private', 'Common'];
export const visibilityChipClass = (v) => (v === 'Common' ? 'chip ok' : 'chip pend');
export const inviteChipClass = (s) =>
  s === 'Confirmed' ? 'chip ok' : s === 'Declined' ? 'chip danger'
    : s === 'Invited' ? 'chip warn' : 'chip pend';

export const sideChipClass = (side) =>
  side === 'Groom' || side === 'Male' ? 'chip male' : side === 'Bride' || side === 'Female' ? 'chip female' : 'chip common';

// Full display name from Surname + (first) Name + Father/Husband parts.
// `nameKey` is the first-name column ('GuestName' for guests, 'MemberName' for party).
export const fullName = (row, nameKey = 'GuestName') =>
  [row.Surname, row[nameKey], row.FatherHusband].map(x => (x || '').trim()).filter(Boolean).join(' ')
  || (row[nameKey] || '').trim() || '—';

// ── Date display / form helpers (API gives 'YYYY-MM-DD' / 'YYYY-MM-DD HH:MM:SS') ─
export const fmtDate = (v) => {
  if (!v) return '—';
  const d = String(v).slice(0, 10).split('-');
  return d.length === 3 ? `${d[2]}-${d[1]}-${d[0]}` : String(v);
};
export const fmtDateTime = (v) => {
  if (!v) return '—';
  const s = String(v);
  const datePart = fmtDate(s);
  const time = s.length >= 16 ? s.slice(11, 16) : '';
  return time ? `${datePart} ${time}` : datePart;
};
// 'YYYY-MM-DD HH:MM:SS' → 'YYYY-MM-DDTHH:MM' for <input type=datetime-local>
export const toDatetimeLocal = (v) => (v ? String(v).replace(' ', 'T').slice(0, 16) : '');
// 'YYYY-MM-DDTHH:MM' → 'YYYY-MM-DD HH:MM:00' for MySQL
export const fromDatetimeLocal = (v) => {
  if (!v) return null;
  const s = String(v).replace('T', ' ');
  return s.length === 16 ? `${s}:00` : s;
};
export const money = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

// Evt_Guests.GuestType - what the guest is to the family. 'Family' is the
// stored value for a relative (older rows and the Cards page use it);
// 'Business' prints the firm on the card (Cards / Labels); 'Home' = the
// hosts' own family: they attend and are counted (headcount, catering) but
// get NO invitation card - Cards and Labels leave them out.
export const GUEST_TYPE_OPTIONS = [
  { value: 'Family', label: 'Relative' },
  { value: 'Friend', label: 'Friend' },
  { value: 'Business', label: 'Business' },
  { value: 'Home', label: 'Own family' },
];
export const NO_CARD_TYPE = 'Home';

// ── Who someone is in the household (Evt_Guests.Relation) ─────────────────
// The column existed and was never filled, so the relationship was being typed
// into the NAME instead - "Dhankani Son", "Ailani Wifi", "Advani Husband" (25
// rows on the live event). A list sorted by name then scatters a family down
// the page, which is exactly what made yesterday's printout unreadable.
//
// `rank` is the order a family is READ in, not alphabetical: the head, his
// wife, each son with his wife and children, then the daughters with theirs,
// then the elders and the rest. The local words sit beside the English ones
// because that is what people actually say here.
export const GUEST_RELATIONS = [
  // The order a household is ALWAYS read in (owner's rule, 2026-10-16):
  // husband · wife · son · daughter-in-law · their children · daughter ·
  // son-in-law · their children · then the elders and the rest. The ranks
  // below ARE that order, so a family needs no hand-sorting to come out right.
  { value: 'Head', label: 'Head of the family', rank: 0 },
  { value: 'Husband', label: 'Husband', rank: 9 },
  { value: 'Wife', label: 'Wife', rank: 10 },
  { value: 'Son', label: 'Son (Beta)', rank: 20 },
  { value: 'Daughter-in-law', label: 'Daughter-in-law (Bhabhi / Nuh)', rank: 21 },
  // The son's children sit under their parents; the daughter's under hers.
  // Both print as plain "Grandson" / "Granddaughter" - the bracket is only
  // there to be picked correctly in the dropdown.
  { value: 'Grandson', label: "Grandson (son's son / Poto)", short: "Grandson (son's)", rank: 22 },
  { value: 'Granddaughter', label: "Granddaughter (son's daughter / Poti)", short: "Granddaughter (son's)", rank: 23 },
  { value: 'Daughter', label: 'Daughter (Beti)', rank: 30 },
  { value: 'Son-in-law', label: 'Son-in-law (Jija / Jamai)', rank: 31 },
  { value: 'Daughters-son', label: "Grandson (daughter's son / Dohito)", short: "Grandson (daughter's)", rank: 32 },
  { value: 'Daughters-daughter', label: "Granddaughter (daughter's daughter / Dohiti)", short: "Granddaughter (daughter's)", rank: 33 },
  { value: 'Father', label: 'Father (Papa)', rank: 40 },
  { value: 'Mother', label: 'Mother (Mummy)', rank: 41 },
  { value: 'Brother', label: 'Brother (Bhai)', rank: 50 },
  { value: 'Sister-in-law', label: "Brother's wife (Bhabhi)", rank: 51 },
  { value: 'Sister', label: 'Sister (Didi / Behen)', rank: 52 },
  { value: 'Brother-in-law', label: "Sister's husband (Jija)", rank: 53 },
  { value: 'Uncle', label: 'Uncle (Kaka / Chacha / Mama / Masa)', rank: 60 },
  { value: 'Aunt', label: 'Aunt (Kaki / Chachi / Mami / Massi)', rank: 61 },
  { value: 'Grandfather', label: 'Grandfather (Dada / Nana)', rank: 62 },
  { value: 'Grandmother', label: 'Grandmother (Dadi / Nani)', rank: 63 },
  { value: 'Cousin', label: 'Cousin', rank: 70 },
  { value: 'Relative', label: 'Relative', rank: 80 },
  { value: 'Friend', label: 'Friend', rank: 85 },
  { value: 'Other', label: 'Other', rank: 90 },
];
export const RELATION_HEAD = 'Head';
const RELATION_BY_VALUE = new Map(GUEST_RELATIONS.map(r => [String(r.value).toLowerCase(), r]));
export const relationDef = (v) => RELATION_BY_VALUE.get(String(v || '').trim().toLowerCase()) || null;
export const relationLabel = (v) => (relationDef(v)?.label || String(v || '').trim());
// For a dropdown, where the full label is too long but "Grandson" alone would
// appear twice - once for the son's child, once for the daughter's.
export const relationShort = (v) => {
  const d = relationDef(v);
  return d ? (d.short || d.label.split(' (')[0]) : String(v || '').trim();
};
// Unknown / blank sorts after everyone named, so a half-filled family still
// reads top to bottom instead of jumping about.
export const relationRank = (v) => (relationDef(v)?.rank ?? 88);

// Evt_Guests.GroupName - the circles a guest list is collected by (Haresh
// Ailani's sheet, Oct 2026). The guest form offers these first, then any other
// group already used in the event; spellings are matched without case.
export const GUEST_GROUPS = ['Family', 'Friends Group', 'Rotary', 'UMA', 'Smart Couple', 'Other Friends', 'Business', 'Ramayan Nagar', 'Own family'];
export const groupKey = (g) => String(g || '').trim().toLowerCase();

// ONE field instead of two (2026-10-14). "Guest is" (Relative / Friend /
// Business / Own family) and "Group" (Family, Friends Group, Rotary …) were
// the same idea asked twice - 74 guests said "Family" in both boxes and 11
// contradicted themselves - so only the GROUP is asked now.
//
// Two groups are not just labels, and the picker says so:
//   Own family   the hosts' own household: counted at every function and by
//                the caterer, but NO card, gift or sweet packet (needsCard)
//   Business     a firm: the card prints the company under the owner's name
// Every other group is an ordinary circle of people. GuestType is still
// WRITTEN, derived from the group, so the card rules, the order counts and
// the printed layouts carry on untouched.
export const OWN_FAMILY_GROUP = 'Own family';
export const BUSINESS_GROUP = 'Business';
export const groupNeedsCard = (g) => groupKey(g) !== groupKey(OWN_FAMILY_GROUP);
export const isFirmGroup = (g) => groupKey(g) === groupKey(BUSINESS_GROUP);
// What goes into Evt_Guests.GuestType for a chosen group. A firm name typed
// for a guest keeps them a business whatever circle they sit in - "Business"
// is a group, but a company on the card is a fact about the card.
export const guestTypeForGroup = (g, companyName) => (
  !groupNeedsCard(g) ? 'Home'
    : (isFirmGroup(g) || String(companyName || '').trim()) ? 'Business'
      : 'Family'
);
// …and the other way round, for rows saved before the merge.
export const groupForGuestType = (t) => (
  String(t) === 'Home' ? OWN_FAMILY_GROUP : String(t) === 'Business' ? BUSINESS_GROUP : ''
);
// What the group picker shows beside a name that carries a rule.
// The value the group pickers use for "a group that is not in the list yet".
// Groups are free text on the guest, so a new one needs no table - typing it
// once puts it in every picker, because the lists are built from the event.
export const NEW_GROUP_VALUE = '__new_group__';
export const groupNote = (g) => (
  !groupNeedsCard(g) ? 'no invitation card' : isFirmGroup(g) ? 'the firm prints on the card' : ''
);
// Any guest whose card is not given: CardStatus 'None' (count-only groups
// mostly). Like the own family, they are counted but get no card / sticker.
export const NO_CARD_STATUS = 'None';
export const needsCard = (r) => String(r?.GuestType || 'Family') !== NO_CARD_TYPE
  && String(r?.CardStatus || '') !== NO_CARD_STATUS;
// A group known only by number ("School Friends - 6"): one row, ExpectedCount
// = persons, GuestName = the group's name (useCountGroup).
export const COUNT_GROUP_TYPE = 'Group';
export const isCountGroup = (r) => String(r?.GuestType || '') === COUNT_GROUP_TYPE;
export const guestTypeLabel = (t) => (GUEST_TYPE_OPTIONS.find(o => o.value === String(t || 'Family')) || GUEST_TYPE_OPTIONS[0]).label;

// ── Added by, short ──────────────────────────────────────────────────────────
// "Ailani Haresh" → "A-H". Only for the PRINTED lists: the same few names
// repeat down every page there, and spelt out they eat the width the guests'
// own names need. The key is printed under the heading so a member who does
// not know the initials can read them off the sheet.
export const adderInitials = (name) => {
  const w = String(name || '').trim().split(/\s+/).filter(Boolean).slice(0, 3);
  return w.map(s => s[0].toUpperCase()).join('-');
};

const capFirst = (s) => (s ? s[0].toUpperCase() + s.slice(1).toLowerCase() : '');
const longerInitials = (name, len) => {
  const w = String(name || '').trim().split(/\s+/).filter(Boolean).slice(0, 3);
  return w.map((s, i) => (i === w.length - 1 ? capFirst(s.slice(0, len)) : s[0].toUpperCase())).join('-');
};

// Short names for a whole list at once, lengthened wherever two people would
// come out the same: Ailani Haresh / Ailani Hiten → A-Ha / A-Hi, never two
// lines of the list claiming the same two letters.
export function adderShortNames(names) {
  const list = [...new Set((names || []).map(n => String(n || '').trim()).filter(Boolean))].sort();
  const byShort = new Map();
  list.forEach((n) => {
    const s = adderInitials(n);
    if (!byShort.has(s)) byShort.set(s, []);
    byShort.get(s).push(n);
  });
  const out = new Map();
  byShort.forEach((group, short) => {
    if (group.length === 1) { out.set(group[0], short); return; }
    for (let len = 2; len <= 8; len += 1) {
      const tries = group.map(n => [n, longerInitials(n, len)]);
      if (new Set(tries.map(t => t[1])).size === group.length) {
        tries.forEach(([n, v]) => out.set(n, v));
        return;
      }
    }
    group.forEach((n, i) => out.set(n, `${short}${i + 1}`));
  });
  return out;
}

// The line printed under the heading: "A-H = Ailani Haresh · A-K = Ailani Kajal".
export const adderLegend = (shortNames) => [...(shortNames || new Map()).entries()]
  .sort((a, b) => a[1].localeCompare(b[1]))
  .map(([name, short]) => `${short} = ${name}`).join(' · ');
